import type { StreetAddress } from '@essent/new-customer';
import { ProductDuration } from '@essent/new-customer';
import capitalize from 'lodash/capitalize';
import type {
  ConsumptionValuesVM,
  Offer,
} from '@innogy/become-a-customer/shared/interfaces';
import type { FilterEntry } from '@legacy/common-ui-utility-components';
import { ContractType } from '@essent/common';
import type { IconIcon } from '@essent/current-web';
import type { QuestionnaireOfferValues } from '@retention/shared';

import type { CommodityOfferContent } from './commodity-offers.model';

export interface CrntIconLabel {
  icon: IconIcon;
  label: string;
}

export const getAddressString = ({
  street,
  houseNumber,
  houseNumberExtension,
  postcode,
  city,
}: Partial<StreetAddress>) =>
  `${street ? street : `${postcode},`} ${houseNumber}${
    houseNumberExtension ? ` ${houseNumberExtension}` : ''
  }${city ? `, ${city}` : ''}`;

export const getConsumptionIconLabels = (
  consumptionValues: ConsumptionValuesVM,
  electricityLabel: string,
  gasLabel: string
): CrntIconLabel[] => {
  const consumptionIconLabels: CrntIconLabel[] = [];

  const electricity = consumptionValues?.electricity ?? {
    supplyLow: 0,
    supplyNormal: 0,
    returnSupplyTotal: 0,
    returnSupplyLow: 0,
    returnSupplyNormal: 0,
  };

  const gas = consumptionValues?.gas ?? 0;

  const { supplyLow, supplyNormal, returnSupplyTotal } = electricity;

  if (supplyNormal > 0) {
    consumptionIconLabels.push({
      icon: supplyLow > 0 ? 'sun' : 'bolt-lightning',
      label: `${supplyNormal} ${electricityLabel}`,
    });
  }

  if (supplyLow > 0) {
    consumptionIconLabels.push({
      icon: 'moon',
      label: `${supplyLow} ${electricityLabel}`,
    });
  }

  if (gas > 0) {
    consumptionIconLabels.push({
      icon: 'fire-flame',
      label: `${gas} ${gasLabel}`,
    });
  }

  if (returnSupplyTotal) {
    consumptionIconLabels.push({
      icon: 'solar-panel',
      label: `${returnSupplyTotal} ${electricityLabel}`,
    });
  }

  return consumptionIconLabels;
};

/**
 * Groups an array of offers into categories based on their contract type (fixed, flex, dynamic).
 * Each contract type will have a corresponding entry in the returned array, containing all offers of that type.
 *
 * @param {Offer[]} offers - The list of offers to be categorized.
 * @returns {FilterEntry<Offer[]>[]} An array of filter entries where each entry represents a contract type and contains its corresponding offers.
 */
export const groupOffersByContractType = (
  offers: Offer[],
  content: CommodityOfferContent | undefined
): FilterEntry<Offer[]>[] => {
  const labels = {
    [ContractType.DYNAMIC]:
      content?.segmentDynamicLabel ?? capitalize(ContractType.DYNAMIC),
    [ContractType.FIXED]:
      content?.segmentFixedLabel ?? capitalize(ContractType.FIXED),
    [ContractType.VARIABLE]:
      content?.segmentFlexLabel ?? capitalize(ContractType.VARIABLE),
  };

  return offers.reduce<FilterEntry<Offer[]>[]>((acc, offer) => {
    const { contractType } = offer;
    let filterCategory = acc.find((category) => category.id === contractType);

    if (!filterCategory) {
      filterCategory = {
        id: contractType as ContractType,
        text: labels[contractType as ContractType],
        value: [],
      };
      acc.push(filterCategory);
    }

    filterCategory.value.push(offer);

    return acc;
  }, []);
};

/**
 * Transforms an array of offers into a list of FilterEntry offer options.
 * Each offer option represents an individual offer, using the offer ID as the identifier.
 *
 * @param {Offer[]} offers - The array of offers to be transformed.
 * @returns {FilterEntry<Offer>[]} A list of offer options where each offer is wrapped in a FilterEntry.
 */
export const getOfferOptions = (offers: Offer[]): FilterEntry<Offer>[] =>
  offers.map((offer) => ({
    id: offer.offerId,
    value: offer,
    text: offer.durationTitle ?? offer.duration,
  }));

export function getSpecificOfferValueFromOffer(
  offer: Offer
): QuestionnaireOfferValues | undefined {
  switch (offer.contractType) {
    case ContractType.DYNAMIC:
      return 'dynamic';
    case ContractType.VARIABLE:
      return 'variable';
    case ContractType.FIXED:
      if (offer.duration === ProductDuration.ONE) {
        return 'fixed1Year';
      } else if (offer.duration === ProductDuration.THREE) {
        return 'fixed3Years';
      }
      return;
    default:
      return;
  }
}
